import { useState } from "react";
import { Loader2, Landmark, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getInvoiceFoundationARPreview, pushInvoiceFoundationAR } from "@/lib/api";
import FoundationARPreviewDialog from "./FoundationARPreviewDialog";

// "Push to Foundation AR" on an invoice: preview (all columns + gates) → confirm → write.
export default function FoundationARPushButton({ invoice, onPushed, testIdSuffix }) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const done = invoice.foundation_ar_status === "success";

  const openPreview = async () => {
    setLoading(true);
    try {
      const { data } = await getInvoiceFoundationARPreview(invoice.id);
      setPreview(data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the Foundation AR preview");
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    setPushing(true);
    try {
      const { data } = await pushInvoiceFoundationAR(invoice.id);
      setResult(data);
      if (data.outcome === "success") {
        toast.success(data.message);
        setOpen(false);
        onPushed?.(data);
      } else {
        toast.error(data.plain_error || data.message);
        setPreview(data.preview || preview);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Foundation AR push failed");
    } finally { setPushing(false); }
  };

  return (
    <>
      {done ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`ar-pushed-chip-${testIdSuffix}`}>
          <CheckCircle2 className="w-3 h-3" /> In Foundation AR {invoice.foundation_ar_invoice_no ? `· ${invoice.foundation_ar_invoice_no}` : ""}
        </span>
      ) : (
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={openPreview} disabled={loading} data-testid={`btn-ar-push-${testIdSuffix}`}>
          {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Landmark className="w-3 h-3 mr-1" />}
          Push to Foundation AR
        </Button>
      )}
      {result && result.outcome !== "success" && (
        <div className="flex flex-col gap-0.5 min-w-0" data-testid={`ar-push-error-${testIdSuffix}`}>
          <span className="text-[10px] text-rose-700">{result.plain_error || result.message}</span>
          {result.exception_text && (
            <pre className="whitespace-pre-wrap break-all font-mono text-[10px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-1.5 py-1" data-testid={`ar-push-raw-error-${testIdSuffix}`}>{result.exception_text}</pre>
          )}
        </div>
      )}
      <FoundationARPreviewDialog open={open} onOpenChange={setOpen} preview={preview} onConfirm={confirm} pushing={pushing}
                                 result={result} testIdPrefix={`ar-${testIdSuffix}`} />
    </>
  );
}
